import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Elements } from "@stripe/react-stripe-js";
import { Typography, Spin, Row, Col } from "antd";
import Modal from "components/Modal/Modal";

import {
  getAllPaymentMethods,
  getSetupIntent,
  changePrimaryPaymentMethod,
  postPaymentMethodId,
} from "services/paymentMethod.service";
import { openNotificationWithIcon } from "utils/Notification";
import { setLoading } from "redux/actions/loading.actions";
import { CheckCircleOutlined, CreditCardOutlined, GoogleOutlined, WalletOutlined } from "@ant-design/icons";

import stripePromise from "lib/stripe";
import StripeCard from "components/StripeCard";
import DigitalWallets from "components/DigitalWallets";


const PAYMENT_METHOD_TYPES = {
  card: "card",
  google: 'google',
  apple: 'apple'
};

function PaymentSetupLoader() {
  return <div style={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 200
  }}><Spin /></div>
}


function AddPaymentMethod({
  message,
  visible,
  handleSave = () => { },
  onCancel = () => { },
  footer,
  forcePrimary,
  currentPrimaryCard,
  saveText = "Save",
  closable = true,
  variant = "inline", // can be "inline" or "modal"
  onSuccess,
  includePrimaryCheckbox = true,
  children,
  sidePanelContent
}) {

  const dispatch = useDispatch();

  const [setupIntent, setSetupIntent] = useState(undefined);

  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState(PAYMENT_METHOD_TYPES.card);

  const { submitting, currentUser } = useSelector((state) => {
    return {
      currentUser: state.user.currentUser,
      submitting: state.loading.submitting,
    };
  });

  useEffect(() => {
    if (visible || variant === "inline") {
      dispatch(getSetupIntent()).then((token) => {
        setSetupIntent(token);
      });
    } else {
      setSetupIntent(undefined);
    }
  }, [visible]);


  async function createPaymentMethodBackend(paymentMethodId = "", isPrimary, merchantType) {
    dispatch(setLoading(true))
    let backendResponse = await dispatch(
      postPaymentMethodId({ paymentMethodId, merchantType, isPrimary })
    );
    if (backendResponse?.status === "success") {
      if (isPrimary) {
        await dispatch(
          changePrimaryPaymentMethod(
            backendResponse?.response?.paymentMethodId,
            currentPrimaryCard?.paymentMethodId,
            false
          )
        );
      }

      if (onSuccess) {
        onSuccess(paymentMethodId)
      } else {
        openNotificationWithIcon(
          "success",
          "Success!",
          "Your payment method has been added successfully"
        );
        dispatch(getAllPaymentMethods()).then((res) => {
          if (handleSave) handleSave(res?.data);
        }).finally(() => dispatch(setLoading(false)));
      }
    }

  }



  function renderPaymentMethod() {
    function renderContent() {
      switch (selectedPaymentMethodType) {
        case PAYMENT_METHOD_TYPES.card:
          return <StripeCard
            setupIntent={setupIntent}
            forcePrimary={forcePrimary}
            saveText={saveText}
            onSave={createPaymentMethodBackend}
            includePrimaryCheckbox={includePrimaryCheckbox}
          >
            {children}
          </StripeCard>;
        case PAYMENT_METHOD_TYPES.google:
          return (
            <DigitalWallets
              setupIntent={setupIntent}
              forcePrimary={forcePrimary}
              onSave={createPaymentMethodBackend}
              includePrimaryCheckbox={includePrimaryCheckbox}
              billingDetails={{
                name: currentUser?.firstName + " " + currentUser?.lastName,
                email: currentUser?.email,
              }}
            >
              {children}
            </DigitalWallets>
          );
        case PAYMENT_METHOD_TYPES.apple:
          return <div style={{ marginTop: 24, textAlign: "center", color: "#7F889A" }}>Apple Pay support coming soon</div>;
        default:
          return null;
      }
    }

    return <div style={{ marginTop: 16 }}>
      {setupIntent ? <Elements stripe={stripePromise} options={{
        clientSecret: setupIntent
      }} >
        {renderContent()}
      </Elements > : <PaymentSetupLoader />}
    </div>

  }

  function renderPaymentMethodTypes() {
    return <Row gutter={[16, 16]} >

      <Col xs={24} md={sidePanelContent ? 12 : 24}>
        <div style={{ display: "flex", gap: 20, marginBottom: 0, justifyContent: 'space-between' }}>
          <div style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            // gap: 8,
            padding: '12px 0px',
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: 1,
            borderRadius: 10,

            border: `2px solid ${selectedPaymentMethodType === PAYMENT_METHOD_TYPES.card ? "#486baf" : "#D9D9D9"}`,
            backgroundColor: selectedPaymentMethodType === PAYMENT_METHOD_TYPES.card ? "#486baf09" : "transparent",
            position: "relative",
            cursor: "pointer"
          }}
            onClick={() => {
              setSelectedPaymentMethodType(PAYMENT_METHOD_TYPES.card)
            }}
          >
            <div>
              <CreditCardOutlined style={{ fontSize: 18 }} />
            </div>
            <div style={{ fontFamily: 'tradeGothic', fontSize: 15, fontWeight: 600, }}>
              Card
            </div>

            {
              selectedPaymentMethodType === PAYMENT_METHOD_TYPES.card && <CheckCircleOutlined style={{ color: "#486baf", position: "absolute", top: 8, right: 8 }} />
            }
          </div>
          <div style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            // gap: 8,
            padding: '12px 0px',
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: 1,
            borderRadius: 10,

            border: `2px solid ${selectedPaymentMethodType === PAYMENT_METHOD_TYPES.google ? "#486baf" : "#D9D9D9"}`,
            backgroundColor: selectedPaymentMethodType === PAYMENT_METHOD_TYPES.google ? "#486baf09" : "transparent",
            position: "relative",
            cursor: "pointer"
          }}
            onClick={() => {
              setSelectedPaymentMethodType(PAYMENT_METHOD_TYPES.google)
            }}
          >
            <div>
              {/* <GoogleOutlined style={{ fontSize: 18 }} /> */}
              <WalletOutlined style={{ fontSize: 18 }} />
            </div>
            <div style={{ fontFamily: 'tradeGothic', fontSize: 15, fontWeight: 600, }}>
              Digital Wallets
            </div>
            {
              selectedPaymentMethodType === PAYMENT_METHOD_TYPES.google && <CheckCircleOutlined style={{ color: "#486baf", position: "absolute", top: 8, right: 8 }} />
            }
          </div>
        </div>


      </Col>
      {
        sidePanelContent && <Col xs={24} md={12} >
          {sidePanelContent}
        </Col>
      }
    </Row>
  }

  if (variant === "inline") {
    return <>
      {renderPaymentMethodTypes()}
      {renderPaymentMethod()}
    </>

  }


  return (
    <Modal
      closable={closable}
      destroyOnClose={true}
      className='stripeAddCardModal'
      headerBorder={false}
      width='460px'
      title='Add New Payment Method'
      onCancel={() => onCancel()}
      visible={visible}
      onOk={() => console.log("ok")}
      okText='Add'
      centered={true}
      footer={[
      ]}
    >
      {renderPaymentMethodTypes()}
      {renderPaymentMethod()}
    </Modal>
  );
}

AddPaymentMethod.propTypes = {};

export default AddPaymentMethod